import { Button, Chip, Stack, styled } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogActions from '../hooks/useProductCatalogActions';
import useProductCatalogFiltering, { FAVORITES_SEARCH_PARAM, PRODUCT_SEARCH_PARAM } from '../hooks/useProductCatalogFiltering';

interface ProductCatalogActiveFilterChip {
  key: string
  label: string
  onDelete: () => void
}

const ActiveFilterChip = styled(Chip)({
  maxWidth: '100%',

  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default function ProductCatalogActiveFilters() {
  const [t] = useTranslation('products');
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const viewProductAttributeValues = useAppSelector(state => state.get('appData').get('viewProductAttributeValues'));
  const {
    clearFavorites,
    clearFilters,
    clearProductSearch,
    removeAttributeFilter,
  } = useProductCatalogActions();
  const {
    activeFiltersByType,
    favoritesSelected,
    productSearchQuery,
  } = useProductCatalogFiltering();

  const activeFilterChips = useMemo(() => {
    const chips: ProductCatalogActiveFilterChip[] = [];

    activeFiltersByType.forEach((filters, typeId) => {
      const type = viewProductAttributeTypes.get(String(typeId));
      if (!type) return;

      filters.forEach(valueId => {
        const value = viewProductAttributeValues.get(String(valueId));
        if (!value) return;

        chips.push({
          key: type.get('slug') + ':' + valueId,
          label: value.get('name'),
          onDelete: () => removeAttributeFilter(typeId, valueId),
        });
      });
    });

    if (favoritesSelected) {
      chips.push({
        key: FAVORITES_SEARCH_PARAM,
        label: t('My favorites'),
        onDelete: clearFavorites,
      });
    }

    if (productSearchQuery.trim() !== '') {
      chips.push({
        key: PRODUCT_SEARCH_PARAM,
        label: t('Search: {{query}}', { query: productSearchQuery.trim() }),
        onDelete: clearProductSearch,
      });
    }

    return chips;
  }, [activeFiltersByType, clearFavorites, clearProductSearch, favoritesSelected, productSearchQuery, removeAttributeFilter, t, viewProductAttributeTypes, viewProductAttributeValues])

  if (activeFilterChips.length === 0) {
    return null;
  }

  return <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
    {activeFilterChips.map(chip => <ActiveFilterChip
      key={chip.key}
      label={chip.label}
      onDelete={chip.onDelete}
      size="small"
      variant="filled"
    />)}
    <Button
      color="secondary"
      size="tiny"
      onClick={clearFilters}
    >{t('Clear filters')}</Button>
  </Stack>;
}
